/** @jsxImportSource @emotion/react */
import { useMemo, useEffect } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { ProjectMetricViewProps } from "@phoenix/pages/project/metrics/types";
import { intShortFormatter } from "@phoenix/utils/numberFormatUtils";
import { ChartTypeRenderer, type ChartType } from "@phoenix/pages/project/metrics/ChartTypeRenderer";
import type { TopKeyAliasesByTokenQuery } from "./__generated__/TopKeyAliasesByTokenQuery.graphql";

const FALLBACK_LABEL = "(unknown)";
const MAX_ITEMS = 10;
const COLOR_PALETTE = ["#0284c7", "#0369a1", "#075985", "#0e7490", "#155e75", "#164e63", "#38bdf8", "#7dd3fc", "#bae6fd", "#e0f2fe"];

interface Props extends ProjectMetricViewProps {
  chartType?: ChartType;
}

export function TopKeyAliasesByToken({ projectId, timeRange, chartType = "vertical_bar", widgetId, widgetTitle, onDataExport }: Props) {
  const data = useLazyLoadQuery<TopKeyAliasesByTokenQuery>(
    graphql`
      query TopKeyAliasesByTokenQuery($projectId: ID!, $timeRange: TimeRange!, $rootSpansOnly: Boolean = true) {
        project: node(id: $projectId) {
          ... on Project {
            spans(timeRange: $timeRange, rootSpansOnly: $rootSpansOnly, first: 500) {
              edges {
                span: node {
                  keyAlias
                  metadata
                  cumulativeTokenCountTotal @include(if: $rootSpansOnly)
                }
              }
            }
          }
        }
      }
    `,
    {
      projectId,
      timeRange: {
        start: timeRange.start?.toISOString(),
        end: timeRange.end?.toISOString(),
      },
      rootSpansOnly: true,
    }
  );

  const chartData = useMemo(() => {
    const edges = data.project?.spans?.edges ?? [];
    const map: Record<string, number> = {};
    for (const { span } of edges) {
      let key = span.keyAlias;
      if (!key && span.metadata) {
        try {
          const meta = typeof span.metadata === "string" ? JSON.parse(span.metadata) : span.metadata;
          // Only use user_api_key_alias - never fall back to user_api_key_hash (security: hash could expose key info)
          key = meta.user_api_key_alias ?? null;
        } catch {
          // ignore
        }
      }
      const label = key || FALLBACK_LABEL;
      map[label] = (map[label] ?? 0) + (span.cumulativeTokenCountTotal ?? 0);
    }
    return Object.entries(map)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, MAX_ITEMS);
  }, [data]);

  useEffect(() => {
    if (chartData.length > 0 && onDataExport && widgetId && widgetTitle) {
      onDataExport(widgetId, widgetTitle, chartData);
    }
  }, [chartData, onDataExport, widgetId, widgetTitle]);

  return (
    <ChartTypeRenderer
      data={chartData}
      chartType={chartType}
      valueFormatter={intShortFormatter}
      colorPalette={COLOR_PALETTE}
      emptyIcon="🔑"
      emptyText="No Key Alias token data"
    />
  );
}
